// Copyright (c) Ping. All rights reserved.

import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { createTheme } from '../design-system/theme';
import { tokens } from '../design-system/tokens';
import { log } from '../log/log';
import { Avatar } from '../components/Avatar';
import { EventMessage } from './EventMessage';
import type { EventMessageType } from './EventMessageType';
import type { EventMessageVariant } from './EventMessageVariant';
import { MessageBubble } from './MessageBubble';
import { NewMessagesDivider } from './NewMessagesDivider';
import { PollCard } from '../polls/legacy';
import type { Poll, PollOptionId } from '../polls/legacy';
import type { ThreadIdentifier, ThreadSummary } from '../threads';
import { ThreadInChat } from '../threads';
import type { UserId } from '../profile';
import type { Message } from '../sync/persistence';
import { deriveThreadAffordance } from './threadAffordance';

/**
 * Cycle 16 Wave 3 WEB-WIRE-B — message-row adapter.
 *
 * Pure controlled view. Wraps `<MessageBubble />` (the regular text row)
 * with Wave-1 / Wave-2 affordances:
 *
 * - optional `<NewMessagesDivider />` above the row
 * - branch for `event` → `<EventMessage />`
 * - branch for `poll`  → `<PollCard />`
 * - `<ThreadInChat />` affordance below the bubble when a summary with
 *   `replyCount > 0` is provided
 * - tappable author avatar that calls back with an anchor `RefObject`
 *   so the host can mount a `<MiniProfileCard />`
 *
 * Renders pure presentation only. No SDK access, no IO. The adapter
 * never logs payload-sensitive fields (`message.body`, poll question /
 * options, thread `previewText`, profile bio); it logs event verbs only
 * with opaque id prefixes.
 *
 * The adapter is `React.memo`'d on referential equality of its inputs.
 * Hosts that mutate `threadSummary` / `poll` / `event` should produce a
 * new reference so the row re-renders, and keep the object stable when
 * nothing changed.
 */

export interface MessageRowEventMeta {
  readonly kind: EventMessageType;
  readonly variant: EventMessageVariant;
  readonly title: string;
  readonly detail?: string | null;
  readonly timestamp: number;
}

export interface MessageRowAdapterProps {
  readonly message: Message;
  /**
   * Host-resolved display name of the sender, used for the avatar initials.
   * The adapter has no roster access, so without this it falls back to a
   * neutral glyph — it must NEVER derive initials from the raw sender id.
   */
  readonly senderName?: string;
  readonly threadSummary?: ThreadSummary | null;
  readonly poll?: Poll | null;
  readonly event?: MessageRowEventMeta | null;
  /**
   * When true, render the `<NewMessagesDivider />` above the row. The
   * host decides which row is the divider anchor (typically the first
   * unread message).
   */
  readonly showNewMessagesDivider?: boolean;
  readonly newMessagesDividerLabel?: string;
  readonly newMessagesUnreadCount?: number;
  readonly onReplyPress?: (message: Message) => void;
  readonly onMentionPress?: (userId: string) => void;
  readonly onAvatarPress?: (userId: UserId, anchorRef: React.RefObject<unknown>) => void;
  readonly onThreadPress?: (identifier: ThreadIdentifier) => void;
  readonly onPollVote?: (optionId: PollOptionId) => void;
  readonly onPollViewVotes?: (poll: Poll) => void;
  readonly testID?: string;
}

const DEFAULT_NEW_MESSAGES_LABEL = 'New messages';

function shortIdPrefix(value: string): string {
  return value.length <= 6 ? value : value.slice(0, 6);
}

const MessageRowAdapterImpl: React.FC<MessageRowAdapterProps> = ({
  message,
  senderName,
  threadSummary,
  poll,
  event,
  showNewMessagesDivider = false,
  newMessagesDividerLabel,
  newMessagesUnreadCount,
  onReplyPress,
  onMentionPress,
  onAvatarPress,
  onThreadPress,
  onPollVote,
  onPollViewVotes,
  testID,
}) => {
  const scheme = useColorScheme();
  const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
  const theme = createTheme(colorScheme);
  const avatarRef = useRef<unknown>(null);

  const affordance = useMemo(() => deriveThreadAffordance(threadSummary), [threadSummary]);

  // Avatar initials — from the host-resolved display name when provided,
  // NEVER the raw sender id (a device hash / account UUID), which would
  // render meaningless hex as "initials". Falls back to a neutral glyph
  // when no resolved name is available.
  const avatarInitials = useMemo(() => {
    const name = senderName?.trim();
    if (name === undefined || name.length === 0) return '?';
    const initials = name
      .split(/\s+/)
      .filter((p: string) => p.length > 0)
      .slice(0, 2)
      .map((p: string) => p.charAt(0))
      .join('');
    return initials.toUpperCase();
  }, [senderName]);

  const handleAvatarPress = useCallback(() => {
    if (onAvatarPress === undefined) {
      return;
    }
    log.debug('messageRow.avatar.press', { uid: shortIdPrefix(message.senderId) });
    onAvatarPress(message.senderId as UserId, avatarRef as React.RefObject<unknown>);
  }, [message.senderId, onAvatarPress]);

  const handleThreadPress = useCallback(
    (summary: ThreadSummary) => {
      log.debug('messageRow.thread.press', {
        tid: shortIdPrefix(String(summary.identifier)),
      });
      onThreadPress?.(summary.identifier);
    },
    [onThreadPress],
  );

  const handlePollVote = useCallback(
    (optionId: PollOptionId) => {
      log.debug('messageRow.poll.vote', { mid: shortIdPrefix(message.id) });
      onPollVote?.(optionId);
    },
    [message.id, onPollVote],
  );

  const handlePollViewVotes = useCallback(
    (p: Poll) => {
      log.debug('messageRow.poll.viewVotes', { mid: shortIdPrefix(message.id) });
      onPollViewVotes?.(p);
    },
    [message.id, onPollViewVotes],
  );

  // EVENT branch: render the divider (if any) + the event row in place
  // of the bubble.
  if (event !== null && event !== undefined) {
    return (
      <View testID={testID}>
        {showNewMessagesDivider ? (
          <NewMessagesDivider
            label={newMessagesDividerLabel ?? DEFAULT_NEW_MESSAGES_LABEL}
            unreadCount={newMessagesUnreadCount}
          />
        ) : null}
        <EventMessage
          kind={event.kind}
          variant={event.variant}
          title={event.title}
          detail={event.detail ?? undefined}
          timestamp={new Date(event.timestamp)}
        />
      </View>
    );
  }

  // POLL branch.
  if (poll !== null && poll !== undefined) {
    return (
      <View testID={testID}>
        {showNewMessagesDivider ? (
          <NewMessagesDivider
            label={newMessagesDividerLabel ?? DEFAULT_NEW_MESSAGES_LABEL}
            unreadCount={newMessagesUnreadCount}
          />
        ) : null}
        <View style={[styles.row, message.isSelf ? styles.rowSelf : styles.rowOther]}>
          {!message.isSelf && onAvatarPress !== undefined ? (
            <Pressable
              ref={avatarRef as React.MutableRefObject<View | null>}
              onPress={handleAvatarPress}
              accessibilityRole="button"
              accessibilityLabel="Open profile"
              style={styles.avatarGutter}
              testID="message-row-avatar"
            >
              <Avatar size="small" initials={avatarInitials} />
            </Pressable>
          ) : null}
          <View style={styles.pollWrap}>
            <PollCard
              poll={poll}
              onVote={onPollVote !== undefined ? handlePollVote : undefined}
              onViewVotes={onPollViewVotes !== undefined ? handlePollViewVotes : undefined}
            />
          </View>
        </View>
      </View>
    );
  }

  // TEXT (bubble) branch.
  return (
    <View testID={testID}>
      {showNewMessagesDivider ? (
        <NewMessagesDivider
          label={newMessagesDividerLabel ?? DEFAULT_NEW_MESSAGES_LABEL}
          unreadCount={newMessagesUnreadCount}
        />
      ) : null}
      <View style={[styles.row, message.isSelf ? styles.rowSelf : styles.rowOther]}>
        {!message.isSelf && onAvatarPress !== undefined ? (
          <Pressable
            ref={avatarRef as React.MutableRefObject<View | null>}
            onPress={handleAvatarPress}
            accessibilityRole="button"
            accessibilityLabel="Open profile"
            style={styles.avatarGutter}
            testID="message-row-avatar"
          >
            <Avatar size="small" initials={avatarInitials} />
          </Pressable>
        ) : null}
        <View style={styles.bubbleColumn}>
          <MessageBubble
            message={message}
            onReplyPress={onReplyPress}
            onMentionPress={onMentionPress}
          />
          {affordance !== null ? (
            <View
              style={[styles.affordanceSlot, { borderColor: theme.colors.border.secondary }]}
              testID="message-row-thread-affordance"
            >
              <ThreadInChat summary={affordance} onOpen={handleThreadPress} />
              {affordance.unreadCount > 0 ? (
                <View
                  style={[styles.unreadDot, { backgroundColor: theme.colors.accent.indigo }]}
                  accessibilityElementsHidden
                />
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

export const MessageRowAdapter = React.memo<MessageRowAdapterProps>((props) => (
  <MessageRowAdapterImpl {...props} />
));
MessageRowAdapter.displayName = 'MessageRowAdapter';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    gap: tokens.spacing.sm,
  } satisfies ViewStyle,
  rowSelf: {
    justifyContent: 'flex-end',
  } satisfies ViewStyle,
  rowOther: {
    justifyContent: 'flex-start',
  } satisfies ViewStyle,
  avatarGutter: {
    paddingTop: 2,
  } satisfies ViewStyle,
  bubbleColumn: {
    flexShrink: 1,
    gap: tokens.spacing.xs,
    minWidth: 0,
  } satisfies ViewStyle,
  pollWrap: {
    flexShrink: 1,
    maxWidth: 420,
  } satisfies ViewStyle,
  affordanceSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingLeft: tokens.spacing.xs,
  } satisfies ViewStyle,
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  } satisfies ViewStyle,
});
